'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2 } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { PHOTO_SCANNING_HASH } from '../constants';
import { useBulkDeleteBatchesMutation } from '../services/photo-scanning.mutations';
import type { ScanBatch } from '../types';

interface PhotoScanningTableActionBarProps {
  table: Table<ScanBatch>;
}

export function PhotoScanningTableActionBar({
  table
}: PhotoScanningTableActionBarProps) {
  const t = useTranslations('photoScanning');
  const bulkDelete = useHashDialog(PHOTO_SCANNING_HASH.bulkDelete);

  const bulkDeleteMutation = useBulkDeleteBatchesMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount = selectedRows.length;

  async function handleBulkDelete() {
    const ids = selectedRows.map((row) => row.original.id);
    try {
      await bulkDeleteMutation.mutateAsync({ ids });
      table.resetRowSelection();
    } catch {
      // handled by global MutationCache
    }
    bulkDelete.onOpenChange(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => bulkDelete.onOpenChange(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      <AlertDialog
        open={bulkDelete.open}
        onOpenChange={bulkDelete.onOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('bulkDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
